import { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { formatDateTime } from "@/lib/utils";
import {
  useAdminPaymentsList,
  type AdminPaymentListItem,
} from "@/lib/admin-api";

const PAYMENTS_PAGE_SIZE = 50;

function paymentTypeLabel(paymentType: string) {
  if (paymentType === "snack_bar") return "Snack Bar";
  if (paymentType === "log_book") return "Log Book";
  return paymentType || "-";
}

export function PaymentsList() {
  const [pageCursor, setPageCursor] = useState<string | undefined>(undefined);
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([
    undefined,
  ]);

  const paymentsQuery = useAdminPaymentsList({
    limit: PAYMENTS_PAGE_SIZE,
    page: pageCursor,
  });
  const payments = paymentsQuery.data?.data ?? [];
  const nextPage = paymentsQuery.data?.nextPage ?? null;
  const loading = paymentsQuery.isFetching;

  const amountBodyTemplate = (rowData: AdminPaymentListItem) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: (rowData.currency || "gbp").toUpperCase(),
    }).format(rowData.amountCents / 100);
  };

  const typeBodyTemplate = (rowData: AdminPaymentListItem) => {
    return paymentTypeLabel(rowData.paymentType);
  };

  const statusBodyTemplate = (rowData: AdminPaymentListItem) => {
    const label =
      rowData.status.length > 0
        ? rowData.status.charAt(0).toUpperCase() + rowData.status.slice(1)
        : rowData.status;
    return <Tag value={label} severity="success" />;
  };

  const dateBodyTemplate = (rowData: AdminPaymentListItem) => {
    return rowData.createdAt ? formatDateTime(rowData.createdAt) : "-";
  };

  const goNext = () => {
    if (!nextPage) return;
    setCursorStack((prev) => [...prev, nextPage]);
    setPageCursor(nextPage);
  };

  const goPrevious = () => {
    if (cursorStack.length <= 1) return;
    const nextStack = cursorStack.slice(0, -1);
    setCursorStack(nextStack);
    setPageCursor(nextStack[nextStack.length - 1]);
  };

  return (
    <div className="card">
      <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-4 gap-4">
        <h1 className="text-2xl font-bold">Ad hoc payments</h1>
        <div className="flex gap-2">
          <Button
            label="Previous"
            icon="pi pi-chevron-left"
            className="p-button-outlined p-button-sm"
            disabled={cursorStack.length <= 1 || loading}
            onClick={goPrevious}
          />
          <Button
            label="Next"
            icon="pi pi-chevron-right"
            iconPos="right"
            className="p-button-outlined p-button-sm"
            disabled={!nextPage || loading}
            onClick={goNext}
          />
        </div>
      </div>

      <DataTable
        value={payments}
        dataKey="id"
        loading={loading}
        responsiveLayout="scroll"
        emptyMessage="No ad hoc payments found"
        className="text-sm"
      >
        <Column
          field="createdAt"
          header="Date"
          body={dateBodyTemplate}
          style={{ minWidth: "160px" }}
        />
        <Column
          field="payerName"
          header="Name"
          style={{ minWidth: "160px" }}
        />
        <Column
          field="paymentType"
          header="Type"
          body={typeBodyTemplate}
          style={{ minWidth: "100px" }}
        />
        <Column
          field="amountCents"
          header="Amount"
          body={amountBodyTemplate}
          style={{ minWidth: "100px" }}
        />
        <Column
          field="status"
          header="Status"
          body={statusBodyTemplate}
          style={{ minWidth: "120px" }}
        />
      </DataTable>
    </div>
  );
}
